/**
 * Crosshair - Center-screen reticle with configurable size, color, and one of
 * six Game UI Kit styles. Five styles match the Figma reticle set
 * (`scope`, `x`, `bracket`, `circle`, `sniper`) — the sixth `dot` is a simple
 * filled-circle utility style kept for FPS use cases.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type CrosshairStyle = 'dot' | 'scope' | 'x' | 'bracket' | 'circle' | 'sniper';

export interface CrosshairOptions extends BaseUIComponentOptions {
  /** Overall reticle size in pixels (drives --ui-crosshair-size). */
  size?: number;
  /** Reticle body color (any CSS color, drives --ui-crosshair-color). */
  color?: string;
  /** Center dot / inner ring accent color (drives --ui-crosshair-accent-color). */
  accentColor?: string;
  /** Reticle style variant from the Game UI Kit. */
  style?: CrosshairStyle;
}

/**
 * SVG markup for each style, drawn in a 100x100 viewBox so the reticle scales
 * with `--ui-crosshair-size`. Body strokes use `currentColor` so `color` flows
 * through; the center accent uses class `ui-crosshair-accent` so the CSS rule
 * paints it with `--ui-crosshair-accent-color`.
 */
const CROSSHAIR_SVG: Record<Exclude<CrosshairStyle, 'dot'>, string> = {
  // Figma Default — outer cyan circle + 4 cardinal outer ticks + red center dot.
  scope: [
    '<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square">',
    '  <circle cx="50" cy="50" r="14" />',
    '  <line x1="50" y1="20" x2="50" y2="32" />',
    '  <line x1="50" y1="68" x2="50" y2="80" />',
    '  <line x1="20" y1="50" x2="32" y2="50" />',
    '  <line x1="68" y1="50" x2="80" y2="50" />',
    '  <circle class="ui-crosshair-accent" cx="50" cy="50" r="2" stroke="none" />',
    '</svg>',
  ].join(''),
  // Figma Variant2 — 4 diagonal lines pointing inward, gap in the middle, red center dot.
  x: [
    '<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">',
    '  <line x1="22" y1="22" x2="38" y2="38" />',
    '  <line x1="78" y1="22" x2="62" y2="38" />',
    '  <line x1="22" y1="78" x2="38" y2="62" />',
    '  <line x1="78" y1="78" x2="62" y2="62" />',
    '  <circle class="ui-crosshair-accent" cx="50" cy="50" r="2" stroke="none" />',
    '</svg>',
  ].join(''),
  // Figma Variant3 — 4 L-shaped corner brackets forming a square frame, red center dot.
  bracket: [
    '<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="miter" stroke-linecap="square">',
    '  <polyline points="32,42 32,32 42,32" />',
    '  <polyline points="58,32 68,32 68,42" />',
    '  <polyline points="32,58 32,68 42,68" />',
    '  <polyline points="58,68 68,68 68,58" />',
    '  <circle class="ui-crosshair-accent" cx="50" cy="50" r="2" stroke="none" />',
    '</svg>',
  ].join(''),
  // Figma Variant4 — large cyan outer ring + small inner red ring.
  circle: [
    '<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2">',
    '  <circle cx="50" cy="50" r="37" />',
    '  <circle class="ui-crosshair-accent" cx="50" cy="50" r="4" fill="none" stroke-width="1.5" />',
    '</svg>',
  ].join(''),
  // Figma Variant5 — sniper scope: outer ring, cross with gap, subdivision ticks, red center dot.
  sniper: [
    '<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square">',
    '  <circle cx="50" cy="50" r="37" />',
    // cross arms (gap in the center)
    '  <line x1="50" y1="13" x2="50" y2="44" />',
    '  <line x1="50" y1="56" x2="50" y2="87" />',
    '  <line x1="13" y1="50" x2="44" y2="50" />',
    '  <line x1="56" y1="50" x2="87" y2="50" />',
    // top vertical subdivision ticks (denser near center)
    '  <line x1="46" y1="20" x2="54" y2="20" stroke-width="1.5" />',
    '  <line x1="47" y1="27" x2="53" y2="27" stroke-width="1.5" />',
    '  <line x1="47" y1="34" x2="53" y2="34" stroke-width="1.5" />',
    // bottom vertical subdivision ticks
    '  <line x1="47" y1="66" x2="53" y2="66" stroke-width="1.5" />',
    '  <line x1="47" y1="73" x2="53" y2="73" stroke-width="1.5" />',
    '  <line x1="46" y1="80" x2="54" y2="80" stroke-width="1.5" />',
    // horizontal subdivision ticks (left + right of center)
    '  <line x1="20" y1="46" x2="20" y2="54" stroke-width="1.5" />',
    '  <line x1="27" y1="47" x2="27" y2="53" stroke-width="1.5" />',
    '  <line x1="34" y1="47" x2="34" y2="53" stroke-width="1.5" />',
    '  <line x1="66" y1="47" x2="66" y2="53" stroke-width="1.5" />',
    '  <line x1="73" y1="47" x2="73" y2="53" stroke-width="1.5" />',
    '  <line x1="80" y1="46" x2="80" y2="54" stroke-width="1.5" />',
    '  <circle class="ui-crosshair-accent" cx="50" cy="50" r="2" stroke="none" />',
    '</svg>',
  ].join(''),
};

export class Crosshair extends BaseUIComponent<CrosshairOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Crosshair',
    category: 'hud',
    summary: 'Center-screen reticle with configurable size, color, and style.',
    useCases: ['crosshair', 'reticle', 'aim', 'cursor', 'shooter'],
    optionsType: 'CrosshairOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/crosshair.html',
      styles: '@engine/assets/ui/styles/crosshair.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private shapeElement: HTMLElement | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: Crosshair.metadata.assetPaths.template,
      stylesPath: Crosshair.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<CrosshairOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      size: 32,
      color: '#36d9d9',
      accentColor: '#ef4444',
      style: 'scope',
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      size: this.options.size,
      color: this.options.color,
      accentColor: this.options.accentColor,
      style: this.options.style,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-crosshair') as HTMLElement | null;
    this.shapeElement = this.layout.querySelector('[data-crosshair-shape]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    this.renderShape(this.options.style);
  }

  /** Update the reticle body color (sets --ui-crosshair-color). */
  public setColor(color: string): void {
    this.options.color = color;
    if (this.rootElement) {
      this.rootElement.style.setProperty('--ui-crosshair-color', color);
    }
  }

  /** Update the accent color used for the center dot / inner ring. */
  public setAccentColor(color: string): void {
    this.options.accentColor = color;
    if (this.rootElement) {
      this.rootElement.style.setProperty('--ui-crosshair-accent-color', color);
    }
  }

  /** Update the reticle size (sets --ui-crosshair-size in px). */
  public setSize(size: number): void {
    this.options.size = size;
    if (this.rootElement) {
      this.rootElement.style.setProperty('--ui-crosshair-size', `${size}px`);
    }
  }

  /** Swap the reticle style variant. */
  public setStyle(style: CrosshairStyle): void {
    if (!this.rootElement) return;
    this.rootElement.classList.remove(`style-${this.options.style}`);
    this.options.style = style;
    this.rootElement.classList.add(`style-${style}`);
    this.renderShape(style);
  }

  private renderShape(style: CrosshairStyle): void {
    if (!this.shapeElement) return;
    // `dot` is rendered as the shape element's own background (see CSS); all
    // other styles inject the matching SVG.
    this.shapeElement.innerHTML = style === 'dot' ? '' : CROSSHAIR_SVG[style];
  }

  protected override onDestroy(): void {
    this.rootElement = null;
    this.shapeElement = null;
  }
}
